import type { Carousel, CarouselSlide } from '@smartapply/shared';

/**
 * Quebra um draft de post em slides de carrossel via heurística determinística.
 *
 * **NÃO usa LLM** — roda 100% no client, instantâneo, zero custo, sem rate limit.
 *
 * Estratégia:
 *   1. Remove hashtags do final do texto (entram opcionalmente no closing).
 *   2. Split em parágrafos (linhas em branco).
 *   3. Ajusta pra ter entre 5 e 8 slides:
 *      - < 5 parágrafos → quebra os longos por frases
 *      - > 8 parágrafos → consolida os curtos adjacentes
 *   4. Cada parágrafo vira 1 slide: primeira frase = título, restante = body.
 *   5. Tenta detectar palavra de destaque (`accentText`) por regex: número,
 *      palavra em CAIXA ALTA, palavra após ênfase ("não", "nunca", "só").
 *   6. Primeiro slide = cover; último = closing; meio = content.
 */
export function buildCarouselFromDraft(draftText: string): Carousel {
  // Normaliza: strip markdown inline (renderer e texto plano editorial),
  // remove hashtags, colapsa quebras de linha excessivas.
  const cleaned = stripInlineMarkdown(draftText)
    .replace(/(\n)?\s*#\w[\w-]*/g, '')
    .trim()
    .replace(/\n{3,}/g, '\n\n');

  // Split em parágrafos.
  let paragraphs = cleaned
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  // Ajusta pra estar no range 5-8.
  if (paragraphs.length < 5) {
    paragraphs = breakLongParagraphsIntoSentences(paragraphs, 5);
  }
  if (paragraphs.length > 8) {
    paragraphs = consolidateShortParagraphs(paragraphs, 8);
  }

  // Garante mínimo 5 (regra do schema). Se o post for muito curto, repete
  // a última frase como filler — situação degenerada, melhor que erro.
  while (paragraphs.length < 5) {
    paragraphs.push('Continue acompanhando a discussão.');
  }

  const slides = paragraphsToSlides(paragraphs);

  return {
    schemaVersion: 1,
    slides,
    generatedAt: new Date().toISOString(),
    modelUsed: 'local-deterministic-v1',
  };
}

/**
 * Strip de markdown inline. O renderer dos slides é texto plano editorial
 * (sem <strong>/<em>), então qualquer `**bold**`, `__bold__` ou heading que
 * o LLM gere precisa virar texto puro pra não vazar asterisco no slide.
 *
 * Conservador: só toca em marcadores claramente intencionais — pares de
 * `**`/`__` cercando texto, e `#`/`##` no início de linha. Não mexe em
 * underscore solto (nomes de variável, URLs) nem em asterisco solto.
 */
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*\n]+?)\*\*/g, '$1')
    .replace(/__([^_\n]+?)__/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '');
}

function paragraphsToSlides(paragraphs: string[]): CarouselSlide[] {
  return paragraphs.map((paragraph, i) => {
    const first = extractFirstSentence(paragraph);
    const rest = paragraph.slice(first.length).trim();

    const kind: CarouselSlide['kind'] =
      i === 0 ? 'cover' : i === paragraphs.length - 1 ? 'closing' : 'content';

    return {
      index: i + 1,
      kind,
      title: clip(first || paragraph, 180),
      body: rest.length > 0 ? clip(rest, 320) : null,
      accentText: detectAccentText(first || paragraph),
      caption: null,
    };
  });
}

/**
 * Pega a primeira frase de um parágrafo: até `.`, `!`, `?`, `…`. Suporta
 * abreviações comuns ("Sr.", "Dr.", "etc.", "ex.") sem quebrar nelas.
 */
function extractFirstSentence(paragraph: string): string {
  // Abreviações comuns que terminam em "." mas não fecham frase.
  const abbreviations = /\b(sr|sra|dr|dra|prof|exa|nº|n°|fl|fls|art|sec|cap|min|seg|hr|hrs|p\.\s?ex|i\.\s?e|e\.\s?g|p\.\s?ex|n\.\s?b)\.\s/gi;

  // Substitui temporariamente abreviações por marcador.
  const protectedText = paragraph.replace(abbreviations, (m) => m.replace('.', '§ABBR§'));

  // Match primeira frase terminada em .!?…
  const match = protectedText.match(/^[^.!?…]+[.!?…]/);
  if (!match) return paragraph;

  return match[0].replace(/§ABBR§/g, '.').trim();
}

/**
 * Detecta palavra-chave de destaque no título via heurística (regex).
 * Retorna null se nada bater — slide fica sem accent.
 */
function detectAccentText(title: string): string | null {
  // 1. Número OU número + unidade ("23 métodos", "R$ 38 mil", "60%").
  const numberWithUnit = title.match(/\bR\$\s?\d[\d.,]*(?:\s?(?:mil|milhão|bilhão|k|m|b))?\b/);
  if (numberWithUnit && numberWithUnit[0].length <= 40) {
    return numberWithUnit[0];
  }
  const percentage = title.match(/\b\d[\d.,]*\s*%/);
  if (percentage && percentage[0].length <= 40) {
    return percentage[0];
  }
  const numWithNoun = title.match(/\b\d[\d.,]*\s+\p{L}{3,}\b/u);
  if (numWithNoun && numWithNoun[0].length <= 40) {
    return numWithNoun[0];
  }
  const plainNumber = title.match(/\b\d[\d.,]{1,6}\b/);
  if (plainNumber && plainNumber[0].length >= 2 && plainNumber[0].length <= 12) {
    return plainNumber[0];
  }

  // 2. Palavra em CAIXA ALTA de 3+ chars (siglas, tecnologias).
  const capsMatch = title.match(/\b[A-ZÀ-Ÿ]{3,}(?:[-_][A-ZÀ-Ÿ0-9]+)*\b/);
  if (capsMatch && capsMatch[0].length <= 40 && capsMatch[0] !== capsMatch[0].slice(0, 1)) {
    return capsMatch[0];
  }

  // 3. Palavra de ênfase: substantivo/adjetivo após "não", "nunca", "só",
  // "apenas", "ainda", "já".
  const emphasis = title.match(
    /(?:n[ãa]o|nunca|s[óo]|apenas|ainda|j[áa])\s+(\p{L}{4,})/u,
  );
  if (emphasis && emphasis[1] && emphasis[1].length <= 30) {
    return emphasis[1];
  }

  // 4. Palavra-chave técnica (PascalCase ou termos com underscore/hífen).
  const techTerm = title.match(/\b[A-Z][a-z]+[A-Z]\w*\b|\b\w+[-_]\w+\b/);
  if (techTerm && techTerm[0].length >= 4 && techTerm[0].length <= 40) {
    return techTerm[0];
  }

  return null;
}

/**
 * Quando temos poucos parágrafos, quebra os mais longos em sub-frases pra
 * atingir o `target` mínimo de slides.
 */
function breakLongParagraphsIntoSentences(paragraphs: string[], target: number): string[] {
  const result = [...paragraphs];
  let safetyMax = 20;

  while (result.length < target && safetyMax > 0) {
    safetyMax -= 1;
    // Acha o parágrafo mais longo.
    let longestIdx = -1;
    let longestLen = 0;
    for (let i = 0; i < result.length; i += 1) {
      const p = result[i];
      if (p && p.length > longestLen) {
        longestLen = p.length;
        longestIdx = i;
      }
    }
    if (longestIdx === -1) break;
    const longest = result[longestIdx];
    if (!longest || longest.length < 120) break;

    // Tenta partir no meio em ponto final ou vírgula.
    const mid = Math.floor(longest.length / 2);
    const splitMatch = longest.slice(mid).match(/[.!?]\s|,\s/);
    if (!splitMatch || splitMatch.index === undefined) break;

    const splitAt = mid + splitMatch.index + splitMatch[0].length;
    const first = longest.slice(0, splitAt).trim();
    const second = longest.slice(splitAt).trim();

    if (first.length < 20 || second.length < 20) break;

    result.splice(longestIdx, 1, first, second);
  }

  return result;
}

/**
 * Quando temos parágrafos demais, agrupa os curtos consecutivos.
 */
function consolidateShortParagraphs(paragraphs: string[], target: number): string[] {
  const result = [...paragraphs];
  let safetyMax = 20;

  while (result.length > target && safetyMax > 0) {
    safetyMax -= 1;
    // Acha 2 parágrafos curtos adjacentes (soma < 320 chars).
    let mergeIdx = -1;
    let smallestSum = Infinity;
    for (let i = 0; i < result.length - 1; i += 1) {
      const a = result[i];
      const b = result[i + 1];
      if (!a || !b) continue;
      const sum = a.length + b.length;
      if (sum < 320 && sum < smallestSum) {
        smallestSum = sum;
        mergeIdx = i;
      }
    }
    if (mergeIdx === -1) break;

    const merged = `${result[mergeIdx] ?? ''}\n\n${result[mergeIdx + 1] ?? ''}`.trim();
    result.splice(mergeIdx, 2, merged);
  }

  return result;
}

function clip(text: string, max: number): string {
  if (text.length <= max) return text;
  // Tenta cortar em palavra completa.
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > max - 40) return cut.slice(0, lastSpace).trim() + '…';
  return cut.trim() + '…';
}

const CAROUSEL_STORAGE_PREFIX = 'smartapply.carousel.';
const CAROUSEL_TTL_MS = 60 * 60 * 1000; // 1 hora

/**
 * Limpa entradas antigas do localStorage (mais de 1h) pra não acumular.
 * Roda antes de salvar nova entrada.
 *
 * As keys têm formato `{timestamp-base36}-{random}` onde o timestamp é
 * `Date.now().toString(36)`. Decodificamos pra comparar com agora.
 */
export function pruneOldCarousels(): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  const toRemove: string[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith(CAROUSEL_STORAGE_PREFIX)) continue;
    const suffix = key.slice(CAROUSEL_STORAGE_PREFIX.length);
    const timestampPart = suffix.split('-')[0];
    if (!timestampPart) continue;
    const timestamp = parseInt(timestampPart, 36);
    if (Number.isNaN(timestamp)) continue;
    if (now - timestamp > CAROUSEL_TTL_MS) {
      toRemove.push(key);
    }
  }
  for (const key of toRemove) {
    window.localStorage.removeItem(key);
  }
}
